import {CreateOrderFormData, OrderDetails, OrderOverviewItem} from "@/models/order.ts";
import {ProblemDetail} from "@/types/problem-details.ts";
import {assertTokenValidForMutation, handleResponse} from "@/services/service-helpers.ts";
import log from '@/lib/logger';

const NBH_ORDERS_BASE_URL = `${import.meta.env.VITE_NBH_URL_ROOT}/api/orders`

export async function getOrders(token: string | null): Promise<OrderOverviewItem[] | void> {
    if (!token) {
        log.error("Kein Token für die Auftragsabfrage vorhanden.");
        return Promise.reject("Kein Token vorhanden");
    }

    const response = await fetch(NBH_ORDERS_BASE_URL, {
        method: "GET",
        headers: new Headers({
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json"
        })
    });

    return handleResponse<OrderOverviewItem[]>(response);
}

export async function getOrderDetails(token: string | null, orderId: string): Promise<OrderDetails | void> {
    if (!token) {
        log.error("Kein Token für die Auftragsabfrage vorhanden.");
        return Promise.reject("Kein Token vorhanden");
    }

    const response = await fetch(`${NBH_ORDERS_BASE_URL}/${orderId}`, {
        method: "GET",
        headers: new Headers({
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json"
        })
    });

    return handleResponse<OrderDetails>(response);
}

export async function createOrder(
    token: string | null,
    orderData: CreateOrderFormData
): Promise<string | void> {
    if (!token) {
        log.error("Can not create order without a valid token.");
        return Promise.reject("No token provided");
    }
    assertTokenValidForMutation()

    const response = await fetch(NBH_ORDERS_BASE_URL, {
        method: "POST",
        headers: new Headers({
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json"
        }),
        body: JSON.stringify(orderData)
    });

    if (response.ok && response.status === 201) {
        const locationHeader = response.headers.get('Location');

        if (locationHeader) {
            const urlParts = locationHeader.split('/');
            return urlParts[urlParts.length - 1];
        }

        return;
    } else if (response.status >= 400 && response.status <= 500 &&
        response.headers.get("Content-Type")?.includes("json")) {
        const error: ProblemDetail = await response.json();
        return Promise.reject(error);
    } else {
        return Promise.reject({
            type: "about:blank",
            title: "Unknown error",
            status: response.status
        });
    }
}

export async function cancelOrder(token: string | null, orderId?: string): Promise<OrderDetails | void> {
    if (!token) {
        log.error("Can not cancel order without a valid token");
        return Promise.reject("No token provided");
    }
    assertTokenValidForMutation()

    const response = await fetch(`${NBH_ORDERS_BASE_URL}/${orderId}`, {
        method: 'PATCH',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
            status: 'Cancelled'
        }),
    });

    return handleResponse<OrderDetails>(response);
}

export interface UpdateOrderRequest {
    clientId: string;
    serviceId: string;
    helperId?: string | null;
    submissionChannelId: string;
    comments?: string;
    mileage?: number | null;
    reimbursableAmount?: number | null;
    payWithPoints?: boolean | null;
    paidFromSocialAccount?: boolean | null;
    donationAmount?: number | null;
}

export async function updateOrder(
    token: string | null,
    orderId: string,
    updateRequest: Partial<UpdateOrderRequest>
): Promise<OrderDetails | void> {
    if (!token) {
        log.error("Can not create order without a valid token.");
        return Promise.reject("No token provided");
    }
    assertTokenValidForMutation()

    const response = await fetch(`${NBH_ORDERS_BASE_URL}/${orderId}`, {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(updateRequest),
    });

    return handleResponse<OrderDetails>(response);
}

export interface OrderCostCalculation {
    mileage: number;
    mileageRate: number;
    mileageCost: number;
    reimbursableAmount: number;
    helperAmount: number;
    activityFees: number;
    totalAmount: number;
    activityPoints: number;
    payWithPoints: boolean;
    hasEnoughPoints: boolean;
    clientPoints: number;
}

export async function getOrderCosts(
    token: string | null,
    orderId: string
): Promise<OrderCostCalculation | void> {
    if (!token) {
        log.error("Can not get order costs without a valid token.");
        return Promise.reject("No token provided");
    }

    const response = await fetch(`${NBH_ORDERS_BASE_URL}/${orderId}/costs`, {
        method: 'GET',
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        }
    });

    return handleResponse<OrderCostCalculation>(response);
}

export async function completeOrder(
    token: string | null,
    orderId: string,
    accountingDate: string,
    donatePointsToSocialAccount = false
): Promise<OrderDetails | void> {
    if (!token) {
        log.error("Can not complete order without a valid token.");
        return Promise.reject("No token provided");
    }
    assertTokenValidForMutation()

    const response = await fetch(`${NBH_ORDERS_BASE_URL}/${orderId}/complete`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({
            accountingDate: accountingDate,
            donatePointsToSocialAccount: donatePointsToSocialAccount
        })
    });

    return handleResponse<OrderDetails>(response);
}

export async function reopenOrder(
    token: string | null,
    orderId: string,
    reason: string
): Promise<OrderDetails | void> {
    if (!token) {
        log.error("Can not reopen order without a valid token.");
        return Promise.reject("No token provided");
    }
    assertTokenValidForMutation()

    const response = await fetch(`${NBH_ORDERS_BASE_URL}/${orderId}/reopen`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({
            reason: reason
        })
    });

    return handleResponse<OrderDetails>(response);
}

export interface OrderEmailPrecheck {
    helperAssigned: boolean;
    helperHasEmail: boolean;
    helperName: string | null;
    helperEmail: string | null;
    emailCapabilityAvailable: boolean;
    templateConfigured: boolean;
    submissionChannelIsEmail: boolean;
}

export interface OrderEmailSendResponse {
    helperName: string;
    helperEmail: string;
}

export async function getOrderEmailPrecheck(
    token: string | null,
    orderId: string,
): Promise<OrderEmailPrecheck | void> {
    if (!token) {
        return Promise.reject("No token provided");
    }

    const response = await fetch(`${NBH_ORDERS_BASE_URL}/${orderId}/email/precheck`, {
        method: 'GET',
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
    });

    return handleResponse<OrderEmailPrecheck>(response);
}

export async function sendOrderEmail(
    token: string | null,
    orderId: string,
    additionalMessage?: string,
): Promise<OrderEmailSendResponse | void> {
    if (!token) {
        return Promise.reject("No token provided");
    }
    assertTokenValidForMutation()

    const response = await fetch(`${NBH_ORDERS_BASE_URL}/${orderId}/email`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            additionalMessage: additionalMessage || null,
        }),
    });

    return handleResponse<OrderEmailSendResponse>(response);
}

export interface OrderAuditLog {
    id: string;
    orderId: string;
    actionType: string;
    description: string;
    createdAt: string;
    createdBy: string;
}

export async function getOrderAuditLogs(
    token: string | null,
    orderId: string
): Promise<OrderAuditLog[] | void> {
    if (!token) {
        log.error("Can not get order audit logs without a valid token.");
        return Promise.reject("No token provided");
    }

    const response = await fetch(`${NBH_ORDERS_BASE_URL}/${orderId}/audit-logs`, {
        method: 'GET',
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        }
    });

    return handleResponse<OrderAuditLog[]>(response);
}

